import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Button, 
  Table, 
  Badge, 
  Nav, 
  Alert,
  Pagination 
} from 'react-bootstrap';
import { FaArrowLeft, FaSync, FaChevronDown, FaChevronRight, FaDownload, FaInfoCircle, FaRedoAlt, FaEye, FaStop } from 'react-icons/fa';
import { useEnvironment } from '../context/EnvironmentContext';
import * as api from '../services/api';
import Spinner from '../components/Spinner';
import { formatDate } from '../utils/helpers';
import './LogsReview.css';

const LogsReview = () => {
  const { agentId, microAgentId, miniAgentId } = useParams();
  const navigate = useNavigate();
  const { activeEnvironment } = useEnvironment();
  
  // Execution list state (List Mode)
  const [executions, setExecutions] = useState([]);
  const [pagination, setPagination] = useState({
    total_count: 0,
    limit: 100,
    offset: 0,
    returned_count: 0,
    has_more: false,
    next_offset: 0
  });
  const [loadingExecutions, setLoadingExecutions] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  
  // Selected execution details state (Detail Mode)
  const [selectedExecution, setSelectedExecution] = useState(null);
  const [executionDetails, setExecutionDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  
  const [error, setError] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const [agent, setAgent] = useState(null);

  // NEW: State for stopping execution
  const [stoppingExecution, setStoppingExecution] = useState({});

  // NEW: State for handling truncated inputs
  const [loadingCompleteInput, setLoadingCompleteInput] = useState(false);

  // Determine the agent ID based on the URL parameters
  const targetId = microAgentId || miniAgentId;
  const targetType = microAgentId ? 'micro-agent' : 'mini-agent';
  const agentTypeDisplay = microAgentId ? 'Micro-Agent' : 'Mini-Agent';

  // Fetch agent details
  useEffect(() => {
    const fetchAgentDetails = async () => {
      try {
        // Get micro agent details using ONLY the micro_agent_id (targetId)
        const response = await api.getMicroAgentsbyId(targetId);
        
        if (response && response.microAgents && response.microAgents.length > 0) {
          const agentData = response.microAgents[0];
          setAgent({
            id: agentData.uniqueIdentifier,
            agentNumber: agentData.agentNumber || '000',
            name: agentData.microAgentName || 'Unnamed Agent',
            description: agentData.microAgentDescription || 'No description available',
            parentAgentName: agentData.parentAgentName || 'Unknown',
            commissioned: agentData.commissioned || false,
            deployedInDev: agentData.deployedInDev || false,
            deployedInProd: agentData.deployedInProd || false
          });
        }
      } catch (err) {
        console.error('Error fetching agent details:', err);
      }
    };

    if (targetId) {
      fetchAgentDetails();
    }
  }, [targetId]);

  // Fetch execution history when component mounts
  useEffect(() => {
    fetchExecutionsList(true); // true = initial load
  }, [targetId]);

  // LIST MODE: Fetch executions list with pagination
  // IMPORTANT: This function retrieves executions using ONLY micro_agent_id (targetId)
  // The micro_agent_number is only used for display purposes, NOT for filtering or retrieval
  const fetchExecutionsList = async (isInitialLoad = false, offset = 0) => {
    if (isInitialLoad) {
      setLoadingExecutions(true);
    } else {
      setLoadingMore(true);
    }
    
    setError(null);
    
    try {
      console.log(`Fetching executions list using ONLY micro_agent_id: ${targetId}, offset: ${offset}`);
      
      // CRITICAL: Use List Mode - get list of executions for the micro-agent
      // We ONLY use micro_agent_id for retrieval, NOT micro_agent_number
      const response = await api.makeAuthenticatedRequest('POST', '/db/micro-agent-executions', {
        micro_agent_id: parseInt(targetId), // ONLY identifier used for retrieval
        limit: 100,
        offset: offset
      });
      
      console.log('Raw executions list response:', response);
      
      // Handle the nested response structure
      let apiResponse = response;
      
      // Check for test_results structure first
      if (response.body?.data?.test_results?.endpoint_response?.body) {
        try {
          console.log('Found test_results structure with string body, parsing...');
          const parsedBody = typeof response.body.data.test_results.endpoint_response.body === 'string' 
            ? JSON.parse(response.body.data.test_results.endpoint_response.body)
            : response.body.data.test_results.endpoint_response.body;
          
          console.log('Successfully parsed test_results body:', parsedBody);
          apiResponse = parsedBody;
        } catch (parseError) {
          console.warn('Failed to parse test_results body:', parseError);
        }
      }
      
      // Fallback to direct data access
      if (!apiResponse.data && response.body?.data) {
        apiResponse = response.body;
      } else if (!apiResponse.data && response.data) {
        apiResponse = { data: response.data };
      }
      
      if (apiResponse.success && apiResponse.data?.executions && Array.isArray(apiResponse.data.executions)) {
        console.log(`Found ${apiResponse.data.executions.length} executions for micro_agent_id: ${targetId}`);
        
        // Transform the executions to match expected format
        // Note: micro_agent_number is kept for DISPLAY purposes only, NOT for filtering
        const transformedExecutions = apiResponse.data.executions.map(exec => ({
          micro_agent_execution_id: exec.micro_agent_execution_id,
          micro_agent_id: exec.micro_agent_id, // Primary identifier
          execution_arn: exec.execution_arn,
          payload: exec.payload,
          outcome: exec.outcome,
          execution_exists: exec.execution_exists,
          duration_seconds: exec.duration_seconds,
          created_at: exec.created_at,
          micro_agent_number: exec.micro_agent_number, // For display only
          micro_agent_name: exec.micro_agent_name,
          is_mini_agent: exec.is_mini_agent,
          agent_id: exec.agent_id,
          agent_name: exec.agent_name,
          // Add formatted fields for UI compatibility
          uniqueIdentifier: String(exec.micro_agent_execution_id),
          agentNumber: exec.micro_agent_number, // Display only
          duration: exec.duration_seconds,
          endtime: calculateEndtime(exec.created_at, exec.duration_seconds),
          executionArn: exec.execution_arn,
          microAgentIdentifier: String(exec.micro_agent_id), // Primary identifier
          result: 'Use "View Details" to see full output',
          starttime: exec.created_at
        }));
        
        console.log('Transformed executions:', transformedExecutions);
        
        if (isInitialLoad) {
          // Initial load - replace all executions
          setExecutions(transformedExecutions);
        } else {
          // Loading more - append to existing executions
          setExecutions(prev => [...prev, ...transformedExecutions]);
        }
        
        // Update pagination info
        if (apiResponse.data.pagination) {
          setPagination(apiResponse.data.pagination);
          console.log('Updated pagination:', apiResponse.data.pagination);
        }
        
      } else {
        console.log('No executions found in response or invalid structure');
        if (isInitialLoad) {
          setExecutions([]);
        }
      }
      
    } catch (err) {
      console.error('Error fetching execution history:', err);
      setError('Failed to load execution history. Please try again.');
    } finally {
      setLoadingExecutions(false);
      setLoadingMore(false);
    }
  };

  // DETAIL MODE: Fetch full execution details including agent_output and execution_log
  // Uses execution_arn as the unique identifier for detailed retrieval
  const fetchExecutionDetails = async (executionArn) => {
    if (!executionArn) return;
    
    setLoadingDetails(true);
    setError(null);
    
    try {
      console.log(`Fetching full execution details using execution_arn: ${executionArn}`);
      
      // Use Detail Mode - get full details for specific execution by ARN
      const response = await api.makeAuthenticatedRequest('POST', '/db/micro-agent-executions', {
        execution_arn: executionArn
      });
      
      console.log('Raw execution details response:', response);
      
      // Handle the nested response structure
      let apiResponse = response;
      
      // Check for test_results structure first
      if (response.body?.data?.test_results?.endpoint_response?.body) {
        try {
          console.log('Found test_results structure for details, parsing...');
          const parsedBody = typeof response.body.data.test_results.endpoint_response.body === 'string' 
            ? JSON.parse(response.body.data.test_results.endpoint_response.body)
            : response.body.data.test_results.endpoint_response.body;
          
          console.log('Successfully parsed test_results details body:', parsedBody);
          apiResponse = parsedBody;
        } catch (parseError) {
          console.warn('Failed to parse test_results details body:', parseError);
        }
      }
      
      // Fallback to direct data access
      if (!apiResponse.data && response.body?.data) {
        apiResponse = response.body;
      } else if (!apiResponse.data && response.data) {
        apiResponse = { data: response.data };
      }
      
      if (apiResponse.success && apiResponse.data) {
        console.log('Successfully fetched execution details:', apiResponse.data);
        setExecutionDetails(apiResponse.data);
      } else {
        throw new Error('Invalid execution details response');
      }
      
    } catch (err) {
      console.error('Error fetching execution details:', err);
      setError('Failed to load execution details. Please try again.');
      setExecutionDetails(null);
    } finally {
      setLoadingDetails(false);
    }
  };

  // Helper function to calculate end time
  const calculateEndtime = (starttime, durationSeconds) => {
    if (!starttime || !durationSeconds) return null;
    const start = new Date(starttime);
    const end = new Date(start.getTime() + durationSeconds * 1000);
    return end.toISOString();
  };

  // Handle execution selection - fetch full details
  const handleSelectExecution = (execution) => {
    console.log('Selected execution:', execution);
    setSelectedExecution(execution);
    
    // Fetch full details for this execution using Detail Mode
    fetchExecutionDetails(execution.execution_arn || execution.executionArn);
  };

  // Handle loading more executions
  const handleLoadMore = () => {
    if (pagination.has_more && !loadingMore) {
      fetchExecutionsList(false, pagination.next_offset);
    }
  };

  const handleRefresh = () => {
    setSelectedExecution(null);
    setExecutionDetails(null);
    setExpandedRows({});
    fetchExecutionsList(true); // true = initial load (reset)
  };

  const handleDownloadLogs = () => {
    if (!executionDetails) return;
    
    const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(executionDetails, null, 2));
    const downloadAnchorNode = document.createElement('a');
    downloadAnchorNode.setAttribute("href", dataStr);
    downloadAnchorNode.setAttribute("download", `execution-details-${selectedExecution.execution_arn.split(':').pop()}.json`);
    document.body.appendChild(downloadAnchorNode);
    downloadAnchorNode.click();
    downloadAnchorNode.remove();
  };

  const toggleRowExpansion = (executionArn) => {
    setExpandedRows(prev => ({
      ...prev,
      [executionArn]: !prev[executionArn]
    }));
  };

  const getStatusBadgeVariant = (status) => {
    if (!status) return 'secondary';
    
    status = status.toLowerCase();
    if (status.includes('success') || status === 'succeeded') return 'success';
    if (status.includes('fail') || status === 'failed') return 'danger';
    if (status.includes('running') || status === 'in progress') return 'primary';
    if (status.includes('abort')) return 'warning';
    return 'secondary';
  };

  // NEW: Function to stop execution
  const handleStopExecution = async (execution, e) => {
    e.stopPropagation(); // Prevent row selection
    
    const executionArn = execution.execution_arn || execution.executionArn;
    if (!executionArn) return;

    try {
      setStoppingExecution(prev => ({ ...prev, [executionArn]: true }));
      
      console.log('Stopping execution:', executionArn);
      
      const response = await api.makeAuthenticatedRequest('POST', '/execution/stop-step-function', {
        executionArn: executionArn
      });
      
      console.log('Stop execution response:', response);
      
      if (response && response.success) {
        // Update the execution status in the list
        setExecutions(prev => 
          prev.map(exec => 
            (exec.execution_arn || exec.executionArn) === executionArn 
              ? { ...exec, outcome: 'stopped' }
              : exec
          )
        );
        // Show success message or toast if available
        console.log('Execution stopped successfully');
      } else {
        throw new Error('Failed to stop execution');
      }
    } catch (err) {
      console.error('Error stopping execution:', err);
      setError('Failed to stop execution: ' + err.message);
    } finally {
      setStoppingExecution(prev => ({ ...prev, [executionArn]: false }));
    }
  };

  // NEW: Function to fetch complete input for truncated payloads
  const fetchCompleteInput = async (executionArn) => {
    try {
      console.log(`Fetching complete input for execution_arn: ${executionArn}`);
      
      // Use Detail Mode to get the full execution details including complete payload
      const response = await api.makeAuthenticatedRequest('POST', '/db/micro-agent-executions', {
        execution_arn: executionArn
      });
      
      console.log('Response for complete input:', response);
      
      // Handle the nested response structure
      let apiResponse = response;
      
      // Check for test_results structure first
      if (response.body?.data?.test_results?.endpoint_response?.body) {
        try {
          const parsedBody = typeof response.body.data.test_results.endpoint_response.body === 'string' 
            ? JSON.parse(response.body.data.test_results.endpoint_response.body)
            : response.body.data.test_results.endpoint_response.body;
          
          apiResponse = parsedBody;
        } catch (parseError) {
          console.warn('Failed to parse test_results body for complete input:', parseError);
        }
      }
      
      // Fallback to direct data access
      if (!apiResponse.data && response.body?.data) {
        apiResponse = response.body;
      } else if (!apiResponse.data && response.data) {
        apiResponse = { data: response.data };
      }
      
      if (apiResponse.success && apiResponse.data?.payload) {
        console.log('Successfully fetched complete input payload:', apiResponse.data.payload);
        return apiResponse.data.payload;
      } else {
        throw new Error('No payload found in response');
      }
      
    } catch (err) {
      console.error('Error fetching complete input:', err);
      throw err;
    }
  };

  // ENHANCED: Helper function to check if payload is truncated
  const isPayloadTruncated = (payload) => {
    if (!payload) return false;
    
    try {
      // If it's a string, check if it looks like truncated JSON
      if (typeof payload === 'string') {
        const trimmed = payload.trim();
        // Check for common truncation indicators
        if (trimmed.endsWith('...') || trimmed.endsWith('[TRUNCATED]')) {
          return true;
        }
        // Check if JSON is incomplete
        try {
          JSON.parse(trimmed);
          return false;
        } catch (e) {
          // If it's not valid JSON but doesn't look like truncation markers, assume it's not truncated
          return trimmed.includes('...') || trimmed.includes('TRUNCATED');
        }
      }
      
      // If it's already an object, it's not truncated
      return false;
    } catch (e) {
      console.error('Error checking payload truncation:', e);
      return false;
    }
  };

  // ENHANCED: Handle retrying an execution with full execution context and complete input handling
  // FIXED: Now properly detects if execution is from a mini-agent and navigates to the correct testing page
  const handleRetryExecution = async (execution, e) => {
    e.stopPropagation(); // Prevent row selection

    try {
      setLoadingCompleteInput(true);
      
      // Extract payload data from the execution
      let payloadData = execution.payload;
      
      // NEW: Check if the payload is truncated
      if (isPayloadTruncated(payloadData)) {
        console.log('Payload appears to be truncated, fetching complete input...');
        
        try {
          // Fetch the complete input using the execution_arn
          const completePayload = await fetchCompleteInput(execution.execution_arn || execution.executionArn);
          
          if (completePayload) {
            console.log('Successfully fetched complete payload, using it instead of truncated version');
            payloadData = completePayload;
          } else {
            console.warn('No complete payload returned from API, using existing payload');
          }
        } catch (fetchError) {
          console.error('Failed to fetch complete input, using existing payload:', fetchError);
          // Continue with the truncated payload if fetch fails
        }
      }
      
      // Try to parse the payload if it's a string
      if (typeof payloadData === 'string') {
        try {
          payloadData = JSON.parse(payloadData);
        } catch (parseError) {
          console.warn("Couldn't parse payload as JSON:", parseError);
          // Use the raw string if parsing fails
        }
      }
      
      // Detect environment from execution ARN
      const detectEnvironmentFromArn = (executionArn) => {
        if (executionArn.includes(':DEV-')) {
          return 'development';
        } else if (executionArn.includes(':PROD-')) {
          return 'production';
        }
        return 'development'; // default to development
      };
      
      const environment = detectEnvironmentFromArn(execution.execution_arn || execution.executionArn);
      
      // ENHANCED: Store comprehensive execution data for full restoration
      const executionContext = {
        payload: payloadData,
        environment: environment,
        executionArn: execution.execution_arn || execution.executionArn,
        executionId: (execution.execution_arn || execution.executionArn).split(':').pop(),
        timestamp: new Date().toISOString(),
        
        // Store execution results for populating the result sections
        executionStatus: {
          status: execution.outcome === 'success' ? 'SUCCEEDED' : 
                 execution.outcome === 'failed' ? 'FAILED' : 
                 execution.outcome?.toUpperCase() || 'COMPLETED',
          startDate: execution.created_at || execution.starttime,
          stopDate: execution.endtime || calculateEndtime(execution.created_at, execution.duration_seconds),
          duration: execution.duration_seconds || execution.duration
        },
        
        // Store execution result data
        executionResult: {
          agentOutput: execution.agent_output,
          executionLog: execution.execution_log,
          outcome: execution.outcome,
          duration: execution.duration_seconds || execution.duration
        },
        
        // Store live logs data (if available in the execution details)
        liveLogs: execution.execution_log || execution.agent_output,
        
        // Flag to indicate this is from logs review
        fromLogsReview: true,
        
        // Store additional metadata
        metadata: {
          microAgentId: execution.micro_agent_id || targetId,
          executionId: execution.micro_agent_execution_id,
          createdAt: execution.created_at,
          microAgentNumber: execution.micro_agent_number || execution.agentNumber,
          microAgentName: execution.micro_agent_name || agent?.name
        }
      };
      
      // Store the comprehensive execution context in localStorage
      localStorage.setItem('retryExecutionPayload', JSON.stringify(executionContext));
      
      console.log('Stored comprehensive execution context for testing page:', executionContext);
      
      // FIXED: Navigate to the correct testing page based on agent type
      // Check if this is a mini-agent execution
      const isMiniAgent = execution.is_mini_agent === true;
      
      if (isMiniAgent) {
        // Navigate to mini-agent testing page
        console.log('Navigating to mini-agent testing page for execution:', execution.micro_agent_id);
        navigate(`/agents/${agentId}/mini-agents/${execution.micro_agent_id}/testing`);
      } else {
        // Navigate to micro-agent testing page
        console.log('Navigating to micro-agent testing page for execution:', execution.micro_agent_id);
        navigate(`/agents/${agentId}/micro-agents/${execution.micro_agent_id}/testing`);
      }
    } catch (error) {
      console.error("Error preparing execution retry:", error);
      setError("Failed to prepare execution retry: " + error.message);
    } finally {
      setLoadingCompleteInput(false);
    }
  };

  const handleGoBack = () => {
    if (microAgentId) {
      navigate(`/agents/${agentId}/micro-agents/${microAgentId}`);
    } else if (miniAgentId) {
      navigate(`/agents/${agentId}/mini-agents/${miniAgentId}`);
    } else {
      navigate(`/agents/${agentId}`);
    }
  };

  // Render execution details panel
  const renderExecutionDetails = () => {
    if (!selectedExecution) {
      return (
        <Alert variant="info">
          <FaInfoCircle className="me-2" />
          Select an execution from the list to view detailed logs and output.
        </Alert>
      );
    }

    if (loadingDetails) {
      return (
        <div className="text-center py-4">
          <Spinner />
          <p className="mt-3">Loading execution details...</p>
        </div>
      );
    }

    if (!executionDetails) {
      return (
        <Alert variant="warning">
          <FaInfoCircle className="me-2" />
          Failed to load execution details. Please try selecting the execution again.
        </Alert>
      );
    }

    return (
      <div>
        {/* Execution Header Info */}
        <Row className="mb-4">
          <Col md={6}>
            <h6>Execution ARN</h6>
            <p className="text-muted small">{executionDetails.execution_arn}</p>
          </Col>
          <Col md={2}>
            <h6>Status</h6>
            <Badge bg={getStatusBadgeVariant(executionDetails.outcome)}>
              {executionDetails.outcome}
            </Badge>
          </Col>
          <Col md={2}>
            <h6>Duration</h6>
            <p className="text-muted">{executionDetails.duration_seconds}s</p>
          </Col>
          <Col md={2}>
            <h6>Created</h6>
            <p className="text-muted">{formatDate(executionDetails.created_at)}</p>
          </Col>
        </Row>

        {/* Agent Output Section */}
        {executionDetails.agent_output && (
          <div className="mb-4">
            <h6 className="mb-3">Agent Output</h6>
            <div className="p-3 bg-light rounded" style={{ maxHeight: '300px', overflowY: 'auto' }}>
              <pre className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                {typeof executionDetails.agent_output === 'object' 
                  ? JSON.stringify(executionDetails.agent_output, null, 2)
                  : executionDetails.agent_output}
              </pre>
            </div>
          </div>
        )}

        {/* Execution Log Section */}
        {executionDetails.execution_log && (
          <div className="mb-4">
            <h6 className="mb-3">Execution Log</h6>
            <div className="p-3 bg-light rounded" style={{ maxHeight: '400px', overflowY: 'auto' }}>
              <pre className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                {typeof executionDetails.execution_log === 'object' 
                  ? JSON.stringify(executionDetails.execution_log, null, 2)
                  : executionDetails.execution_log}
              </pre>
            </div>
          </div>
        )}

        {/* Payload Section */}
        <div className="mb-4">
          <h6 className="mb-3">Input Payload</h6>
          <div className="p-3 bg-light rounded" style={{ maxHeight: '200px', overflowY: 'auto' }}>
            <pre className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>
              {executionDetails.payload ? 
                (typeof executionDetails.payload === 'string' ? 
                  // Try to parse and format JSON payload
                  (() => {
                    try {
                      return JSON.stringify(JSON.parse(executionDetails.payload), null, 2);
                    } catch (e) {
                      return executionDetails.payload;
                    }
                  })() : 
                  JSON.stringify(executionDetails.payload, null, 2)) : 
                'No payload data available'}
            </pre>
          </div>
        </div>

        {/* Additional Metadata */}
        <div className="mb-4">
          <h6 className="mb-3">Execution Metadata</h6>
          <div className="p-3 bg-light rounded">
            <Row>
              <Col md={6}>
                <p><strong>Micro-Agent ID:</strong> {executionDetails.micro_agent_id}</p>
                <p><strong>Micro-Agent Name:</strong> {executionDetails.micro_agent_name}</p>
                <p><strong>Agent Number:</strong> {executionDetails.micro_agent_number} <small className="text-muted">(display only)</small></p>
                <p><strong>Is Mini-Agent:</strong> {executionDetails.is_mini_agent ? 'Yes' : 'No'}</p>
              </Col>
              <Col md={6}>
                <p><strong>Parent Agent:</strong> {executionDetails.agent_name}</p>
                <p><strong>Execution Exists:</strong> {executionDetails.execution_exists ? 'Yes' : 'No'}</p>
              </Col>
            </Row>
          </div>
        </div>
      </div>
    );
  };

  if (loadingExecutions && executions.length === 0) {
    return (
      <Container className="py-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1>Execution Logs</h1>
          <Button variant="outline-secondary" onClick={handleGoBack}>
            <FaArrowLeft className="me-2" />
            Back to Agent
          </Button>
        </div>
        <div className="text-center py-5">
          <Spinner />
          <p className="mt-3">Loading execution history...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1>Execution Logs</h1>
          <p className="text-muted">
            {agentTypeDisplay}: {agent?.name || targetId}
            <br />
            <small>Retrieving executions by Micro-Agent ID: {targetId}</small>
          </p>
        </div>
        <div>
          <Button variant="outline-secondary" onClick={handleGoBack} className="me-2">
            <FaArrowLeft className="me-2" />
            Back to Agent
          </Button>
          <Button variant="outline-primary" onClick={handleRefresh}>
            <FaSync className="me-2" />
            Refresh
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      )}

      {/* NEW: Loading indicator for complete input fetch */}
      {loadingCompleteInput && (
        <Alert variant="info" className="mb-4">
          <Spinner size="sm" className="me-2" />
          Fetching complete input data...
        </Alert>
      )}
      
      {/* Agent Info Card */}
      {agent && (
        <Card className="mb-4">
          <Card.Header>
            <h4 className="mb-0">Agent Information</h4>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <div className="agent-info-details">
                  <div className="info-item">
                    <span className="info-label">Name:</span>
                    <span className="info-value">{agent.name}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Description:</span>
                    <span className="info-value">{agent.description}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Parent Agent:</span>
                    <span className="info-value">{agent.parentAgentName}</span>
                  </div>
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-3">
                  <strong>Micro-Agent ID:</strong> {agent.id}
                </div>
                <div className="mb-3">
                  <strong>Agent Number (Display Only):</strong> {agent.agentNumber}
                </div>
                <div className="mb-3">
                  <strong>Development Status:</strong>{' '}
                  <Badge bg={agent.deployedInDev ? 'success' : 'secondary'}>
                    {agent.deployedInDev ? 'Deployed' : 'Not Deployed'}
                  </Badge>
                </div>
                <div className="mb-3">
                  <strong>Production Status:</strong>{' '}
                  <Badge bg={agent.deployedInProd ? 'success' : 'secondary'}>
                    {agent.deployedInProd ? 'Deployed' : 'Not Deployed'}
                  </Badge>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      <Row>
        {/* Left Column: Executions List */}
        <Col lg={selectedExecution ? 6 : 12}>
          <Card className="mb-4">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h4 className="mb-0">
                Recent Executions ({pagination.total_count > 0 ? pagination.total_count : executions.length})
              </h4>
              <div className="d-flex align-items-center">
                {pagination.total_count > 0 && (
                  <small className="text-muted me-3">
                    Showing {executions.length} of {pagination.total_count}
                  </small>
                )}
                <Button variant="outline-primary" size="sm" onClick={handleRefresh} disabled={loadingExecutions}>
                  <FaSync className={loadingExecutions ? 'fa-spin' : ''} />
                </Button>
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              {executions.length === 0 ? (
                <div className="text-center py-4">
                  <p>No execution history found for this agent.</p>
                </div>
              ) : (
                <div>
                  <div className="executions-table-container" style={{ maxHeight: '500px', overflow: 'auto' }}>
                    <Table responsive hover className="mb-0">
                      <thead>
                        <tr>
                          <th style={{ width: '40px', position: 'sticky', top: 0, background: '#f8f9fa', zIndex: 1 }}></th>
                          <th style={{ position: 'sticky', top: 0, background: '#f8f9fa', zIndex: 1 }}>Execution ID</th>
                          <th style={{ position: 'sticky', top: 0, background: '#f8f9fa', zIndex: 1 }}>Created</th>
                          <th style={{ position: 'sticky', top: 0, background: '#f8f9fa', zIndex: 1 }}>Status</th>
                          <th style={{ position: 'sticky', top: 0, background: '#f8f9fa', zIndex: 1 }}>Duration</th>
                          <th style={{ position: 'sticky', top: 0, background: '#f8f9fa', zIndex: 1 }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {executions.map((execution) => {
                          const executionArn = execution.execution_arn || execution.executionArn;
                          const isRunning = execution.outcome && execution.outcome.toLowerCase() === 'running';
                          const isStopping = stoppingExecution[executionArn];
                          
                          return (
                            <React.Fragment key={execution.micro_agent_execution_id}>
                              <tr 
                                onClick={() => handleSelectExecution(execution)}
                                className={selectedExecution?.micro_agent_execution_id === execution.micro_agent_execution_id ? 'table-active' : ''}
                                style={{ cursor: 'pointer' }}
                              >
                                <td>
                                  <Button 
                                    variant="link" 
                                    size="sm"
                                    className="p-0 text-muted"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleRowExpansion(execution.execution_arn);
                                    }}
                                  >
                                    {expandedRows[execution.execution_arn] ? <FaChevronDown /> : <FaChevronRight />}
                                  </Button>
                                </td>
                                <td>
                                  <code className="small">
                                    {execution.execution_arn?.split(':').pop().substring(0, 12) || 'N/A'}...
                                  </code>
                                </td>
                                <td>{formatDate(execution.created_at)}</td>
                                <td>
                                  <Badge bg={getStatusBadgeVariant(execution.outcome)}>
                                    {execution.outcome || 'Unknown'}
                                  </Badge>
                                </td>
                                <td>
                                  {execution.duration_seconds ? 
                                    `${execution.duration_seconds}s` : 
                                    'N/A'}
                                </td>
                                <td>
                                  <div className="d-flex gap-1">
                                    <Button
                                      variant="outline-info"
                                      size="sm"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleSelectExecution(execution);
                                      }}
                                      title="View full execution details"
                                    >
                                      <FaEye />
                                    </Button>
                                    <Button
                                      variant="outline-primary"
                                      size="sm"
                                      onClick={(e) => handleRetryExecution(execution, e)}
                                      title="Autofill this execution data in testing page"
                                      disabled={loadingCompleteInput}
                                    >
                                      {loadingCompleteInput ? <Spinner size="sm" /> : <FaRedoAlt />}
                                    </Button>
                                    {/* NEW: Stop execution button for running executions */}
                                    {isRunning && (
                                      <Button
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={(e) => handleStopExecution(execution, e)}
                                        title="Stop this execution"
                                        disabled={isStopping}
                                      >
                                        {isStopping ? (
                                          <Spinner size="sm" />
                                        ) : (
                                          <FaStop />
                                        )}
                                      </Button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                              {expandedRows[execution.execution_arn] && (
                                <tr>
                                  <td colSpan="6" className="p-0">
                                    <div className="p-3 bg-light">
                                      <h6>Payload Summary:</h6>
                                      <pre className="mb-0 p-2 bg-white small" style={{ maxHeight: '150px', overflow: 'auto' }}>
                                        {execution.payload ? 
                                          (typeof execution.payload === 'string' ? 
                                            // Try to parse and format JSON payload
                                            (() => {
                                              try {
                                                return JSON.stringify(JSON.parse(execution.payload), null, 2);
                                              } catch (e) {
                                                return execution.payload;
                                              }
                                            })() : 
                                            JSON.stringify(execution.payload, null, 2)) : 
                                          'No payload data available'}
                                      </pre>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </Table>
                  </div>
                  
                  {/* Load More Button */}
                  {pagination.has_more && (
                    <div className="p-3 border-top text-center">
                      <Button 
                        variant="outline-primary" 
                        onClick={handleLoadMore}
                        disabled={loadingMore}
                      >
                        {loadingMore ? (
                          <>
                            <Spinner size="sm" className="me-2" />
                            Loading More...
                          </>
                        ) : (
                          <>
                            Load More Executions ({pagination.total_count - executions.length} remaining)
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                  
                  {/* Pagination Info */}
                  {pagination.total_count > 0 && (
                    <div className="p-2 bg-light text-center small text-muted">
                      Showing {executions.length} of {pagination.total_count} executions
                    </div>
                  )}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Right Column: Execution Details (when an execution is selected) */}
        {selectedExecution && (
          <Col lg={6}>
            <Card>
              <Card.Header className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Execution Details</h5>
                <div>
                  <Button 
                    variant="outline-secondary" 
                    size="sm"
                    onClick={handleDownloadLogs}
                    disabled={loadingDetails || !executionDetails}
                    className="me-2"
                  >
                    <FaDownload className="me-1" />
                    Download
                  </Button>
                  <Button 
                    variant="outline-secondary" 
                    size="sm"
                    onClick={() => {
                      setSelectedExecution(null);
                      setExecutionDetails(null);
                    }}
                  >
                    Close
                  </Button>
                </div>
              </Card.Header>
              <Card.Body style={{ maxHeight: '600px', overflowY: 'auto' }}>
                {renderExecutionDetails()}
              </Card.Body>
            </Card>
          </Col>
        )}
      </Row>
    </Container>
  );
};

export default LogsReview;